import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { cn } from "@/lib/utils";
import SEO from "@/components/SEO";

const PRIMARY_CTA_URL =
  "https://greyrockai.app/?utm_source=seo&utm_medium=landing&utm_campaign=grey-rock-method-examples";
const SECONDARY_CTA_URL =
  "https://greyrockai.app/?utm_source=seo&utm_medium=landing&utm_campaign=grey-rock-method-examples-bottom";

// Same styling as the homepage hero CTA (PasteAnalyzer submit button).
const ctaClass = cn(
  "inline-flex items-center justify-center gap-2 px-8 h-12 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-black text-sm font-semibold tracking-wide transition-all",
  "shadow-[0_0_40px_-6px_rgba(34,211,238,0.85)] hover:shadow-[0_0_60px_-4px_rgba(34,211,238,1)] animate-cyanPulse"
);

const FAQ = [
  {
    q: "Does the grey rock method actually work on narcissists?",
    a: "It works in the sense of removing the emotional fuel that powers the manipulation. It doesn't change the narcissist. It changes the dynamic between you and them — they get less of what they were extracting, which usually means they escalate first, then disengage. The escalation phase is the hardest part. Most people who try grey rocking quit during that phase because the manipulation gets worse before it gets better.",
  },
  {
    q: "How long does it take to see results from grey rocking?",
    a: "In our experience watching users work through this: 2-6 weeks for the first noticeable change in the other person's behavior, and 2-3 months for stable de-escalation. The first two weeks usually involve increased manipulation as they try to provoke a reaction. Consistency through that window is what makes the method work.",
  },
  {
    q: "Can I use the grey rock method in text conversations?",
    a: "Yes, and text is often where grey rocking is easier to apply because you have time to think before responding. The harder cases are live conversations where you have seconds to react. Text-based grey rocking is where most people start — and where GreyRock AI was originally designed to help.",
  },
  {
    q: "Is grey rocking the same as no contact?",
    a: "No. No contact is full disengagement — you stop responding entirely. Grey rocking is responding minimally and unprovocatively while maintaining necessary communication (coparenting, workplace dynamics, family relationships you can't fully exit). Grey rocking is for situations where you have to stay in contact but want to remove the emotional payoff. No contact is for situations where you can leave entirely.",
  },
  {
    q: "What if grey rocking makes them angry?",
    a: "It usually does, at first. Removing the emotional reaction they were extracting is itself a kind of withdrawal — and manipulators often respond to perceived withdrawal with escalation. This is the part of the method that most articles don't prepare you for. The anger is usually the sign that the technique is working, not failing. It means they're no longer getting the response they were trying to provoke. The strategy is to hold steady through the escalation phase, not to escalate back.",
  },
];

const EXAMPLES = [
  {
    n: 1,
    title: "Emotional invalidation",
    they: "You're being way too sensitive about this.",
    tactic:
      "Invalidation. They're not addressing what you said — they're labeling your reaction as the problem so they don't have to engage with the substance.",
    response: "I see it differently.",
    why: "You don't defend, explain, or push back. You don't argue that you're not too sensitive — that would accept the frame. You acknowledge the difference without absorbing the label.",
  },
  {
    n: 2,
    title: 'Social pressure / "everyone agrees"',
    they: "Everyone agrees with me on this. You're the only one who has a problem.",
    tactic:
      'Social pressure. They\'re invoking an invisible jury to make you feel isolated and outnumbered. The "everyone" is rhetorical — it doesn\'t exist as a group with a unified opinion.',
    response: "Then I'm comfortable being the one who sees it differently.",
    why: 'You don\'t ask who "everyone" is (that escalates). You don\'t agree (that absorbs the manipulation). You accept the isolation they\'re trying to use against you and remove its sting.',
  },
  {
    n: 3,
    title: 'Conditional love / "if you loved me"',
    they: "If you really loved me, you would do this.",
    tactic:
      "Conditional affection. Love is being weaponized as a measurement test. Compliance becomes proof of love, refusal becomes proof of betrayal.",
    response: "Love doesn't work like that for me.",
    why: "You don't argue the premise. You don't try to prove you love them. You name that the framework itself is wrong without making it a fight.",
  },
  {
    n: 4,
    title: 'The retroactive reframe / "I was just joking"',
    they: "I was just joking. You're taking this way too seriously.",
    tactic:
      "Retroactive reframe. They said something hurtful, you reacted, and now they're rewriting the event so your reaction looks like the overreaction. This is sometimes called the Schrödinger's joke — it's a real opinion if you don't push back, and \"just a joke\" if you do.",
    response: "I'm not sure it landed as a joke.",
    why: "You don't accept the reframe (that would make your reaction the problem). You don't accuse them of lying (that escalates). You name what happened from your side without making it a debate.",
  },
  {
    n: 5,
    title: "Future faking",
    they: "Things will be different once we [move / get married / have kids / get past this stressful period].",
    tactic:
      'Future faking. A promise about the future is being used to bypass a present grievance. The future thing keeps moving — once they move, the goalpost becomes "once we get married," then "once we have kids."',
    response: "I'm focused on how things are now.",
    why: "You don't argue that the future thing won't fix it. You don't refuse outright. You quietly stop letting the future hypothetical do the work the present moment requires.",
  },
  {
    n: 6,
    title: "The DARVO (deny, attack, reverse victim and offender)",
    they: "I can't believe you're attacking me right now. You're being so cruel.",
    tactic:
      "DARVO. You raised a legitimate concern, they're flipping the script to make you the aggressor and themselves the victim. Now you're defending yourself instead of addressing the original issue.",
    response: "I'm not attacking you. I'm naming what happened.",
    why: "You don't apologize (which would confirm their frame). You don't escalate (which would prove their frame). You hold the original issue while declining the new role they're assigning you.",
  },
];

const Blockquote = ({ children }: { children: React.ReactNode }) => (
  <blockquote className="my-4 border-l-2 border-cyan-400/50 bg-white/[0.03] px-5 py-3 rounded-r-md text-white/80 italic font-serif text-[17px] leading-relaxed">
    {children}
  </blockquote>
);

const Example = ({ ex }: { ex: (typeof EXAMPLES)[number] }) => (
  <article className="mt-10 first:mt-6">
    <h3 className="text-[15px] md:text-base font-mono uppercase tracking-[0.18em] text-cyan-300/85 italic mb-3">
      Example {ex.n}: {ex.title}
    </h3>
    <p className="text-white/55 text-[14px] uppercase tracking-[0.18em] font-mono mb-1">
      They said
    </p>
    <Blockquote>"{ex.they}"</Blockquote>
    <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/80 mt-4">
      <span className="text-white font-semibold">The tactic:</span> {ex.tactic}
    </p>
    <p className="text-white/55 text-[14px] uppercase tracking-[0.18em] font-mono mt-5 mb-1">
      Grey rock response
    </p>
    <Blockquote>"{ex.response}"</Blockquote>
    <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/80 mt-4">
      <span className="text-white font-semibold">Why it works:</span> {ex.why}
    </p>
  </article>
);

const GreyRockMethodExamples = () => {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div
      className="min-h-screen bg-black text-white"
      style={{
        background:
          "radial-gradient(ellipse at top, rgba(34,211,238,0.06) 0%, rgba(0,0,0,1) 60%)",
      }}
    >
      <SEO
        title="Grey Rock Method: Real Examples From Real Conversations | GreyRock AI"
        description="What the grey rock method actually looks like in practice. Real examples of grey rock responses to manipulation, gaslighting, and emotional pressure. No generic advice."
        path="/grey-rock-method-examples"
      />
      <Helmet>
        <meta
          property="og:title"
          content="Grey Rock Method Examples — Real Responses To Real Manipulation"
        />
        <meta
          property="og:description"
          content="Six calibrated grey rock examples for the manipulation tactics you actually encounter. Not generic advice."
        />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://greyrockai.app/grey-rock-method-examples" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      {/* Slim top bar */}
      <header className="px-5 py-4 border-b border-white/[0.06]">
        <div className="max-w-[720px] mx-auto flex items-center justify-between">
          <Link
            to="/"
            className="text-[11px] font-mono uppercase tracking-[0.25em] text-cyan-300/80 hover:text-cyan-200 transition-colors"
          >
            ← GreyRock AI®
          </Link>
          <a
            href={PRIMARY_CTA_URL}
            className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/55 hover:text-cyan-200 transition-colors"
          >
            Try the analyzer →
          </a>
        </div>
      </header>

      <main className="px-5 py-12 md:py-20">
        <article className="max-w-[720px] mx-auto">
          {/* Hero */}
          <h1 className="text-[2rem] md:text-[3rem] font-semibold tracking-tight leading-[1.1] mb-8">
            Grey Rock Method Examples
          </h1>
          <p className="text-[18px] md:text-[19px] leading-[1.7] text-white/80">
            What the grey rock method actually looks like when you're in the middle of a
            manipulative conversation — not the textbook version.
          </p>
          <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/70 mt-4">
            Most articles tell you to "stay calm and give short answers." That advice fails the
            moment a real manipulator pushes back. The grey rock method works, but only if your
            responses are calibrated to what the other person is actually doing.
          </p>
          <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/70 mt-4">
            This page shows you what calibrated grey rock looks like — in real exchanges, with the
            manipulation tactic named, and the response that defuses it.
          </p>

          {/* Section 2 */}
          <section className="mt-14">
            <h2 className="text-[1.5rem] md:text-[1.875rem] font-semibold tracking-tight leading-[1.2] mb-5">
              What grey rocking actually is (and isn't)
            </h2>
            <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/75">
              Grey rocking is named after the idea of making yourself as uninteresting as a grey
              rock — emotionally unreactive, low-information, impossible to provoke.
            </p>
            <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/75 mt-4">
              But the version most people read about online treats it like a personality.{" "}
              <em>Become emotionally distant. Stay unreactive. Don't engage.</em>
            </p>
            <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/75 mt-4">
              That's incomplete. Real manipulators escalate when their usual tactics stop working.
              A flat "okay" doesn't end the conversation — it triggers a different tactic, often
              more aggressive than the first.
            </p>
            <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/75 mt-4">
              Effective grey rocking is <em>responsive</em>, not flat. Each response is calibrated
              to defuse the specific tactic being used. The goal isn't to disappear. The goal is to
              remove the emotional payoff the manipulation was designed to extract.
            </p>
            <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/75 mt-4">
              The examples below show this in practice.
            </p>
          </section>

          {/* Section 3 — examples */}
          <section className="mt-14">
            <h2 className="text-[1.5rem] md:text-[1.875rem] font-semibold tracking-tight leading-[1.2] mb-3">
              Real grey rock examples
            </h2>
            <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/70">
              Each example shows: the manipulative message, the tactic being used, and a
              calibrated grey rock response.
            </p>
            {EXAMPLES.map((ex) => (
              <Example key={ex.n} ex={ex} />
            ))}
          </section>

          {/* Section 4 */}
          <section className="mt-14">
            <h2 className="text-[1.5rem] md:text-[1.875rem] font-semibold tracking-tight leading-[1.2] mb-5">
              Why these examples are a starting point, not a script
            </h2>
            <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/75">
              These six examples cover the most common manipulation tactics, but real
              conversations rarely fit the template cleanly. Most manipulators use multiple tactics
              in the same message — a single sentence might combine invalidation, future faking,
              and a soft DARVO.
            </p>
            <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/75 mt-4">
              The right grey rock response depends on what they're <em>actually</em> doing, not
              what you think they're doing. In the moment, that's almost impossible to read
              accurately. Your nervous system is activated, you're trying to manage your reaction,
              and you have seconds to choose what to say next.
            </p>
            <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/75 mt-4">
              That's the gap.
            </p>
            <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/75 mt-4">
              You can read every grey rock article online and still freeze when the message hits
              your phone. Not because you don't understand the technique — because identifying{" "}
              <em>which</em> tactic is being used, in real time, while you're being targeted by it,
              is a different skill than reading examples on a page.
            </p>
          </section>

          {/* Section 5 + mid CTA */}
          <section className="mt-14">
            <h2 className="text-[1.5rem] md:text-[1.875rem] font-semibold tracking-tight leading-[1.2] mb-5">
              What to do when you can't read the room
            </h2>
            <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/75">
              GreyRock AI was built for this. You paste in the actual message you received, and it
              identifies which manipulation tactic is being used — including the messy cases where
              multiple tactics overlap. Then it generates calibrated grey rock responses to that
              specific message.
            </p>
            <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/75 mt-4">
              It's not generic advice. It reads what <em>they</em> said and shows you what's
              actually happening — so the response you choose matches what's actually in the
              conversation, not a template from an article.
            </p>

            <div className="mt-8 text-center">
              <a href={PRIMARY_CTA_URL} className={ctaClass}>
                Try It Free — No Signup Required →
              </a>
            </div>

            <p className="text-[15px] md:text-[16px] leading-[1.7] text-white/55 mt-6 text-center max-w-lg mx-auto">
              You get three free analyses to see how it works. If you want full access — unlimited
              analyses, response rehearsals, pattern tracking over time — there's a paid tier. But
              you can see whether it's useful before you commit to anything.
            </p>
          </section>

          {/* Section 6 — FAQ */}
          <section className="mt-14">
            <h2 className="text-[1.5rem] md:text-[1.875rem] font-semibold tracking-tight leading-[1.2] mb-6">
              Common questions about grey rocking
            </h2>
            {FAQ.map((f) => (
              <div key={f.q} className="mt-8 first:mt-0">
                <h3 className="text-[17px] md:text-[19px] font-semibold tracking-tight text-white mb-3 leading-snug">
                  {f.q}
                </h3>
                <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/75">{f.a}</p>
              </div>
            ))}
          </section>

          {/* Section 7 — soft close */}
          <section className="mt-16">
            <h2 className="text-[1.5rem] md:text-[1.875rem] font-semibold tracking-tight leading-[1.2] mb-5">
              More on patterns and responses
            </h2>
            <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/75">
              If you found this useful, GreyRock AI continues the work. It reads your real
              conversations, identifies the patterns you couldn't name in the moment, and generates
              responses calibrated to what's actually being done to you. It's part of{" "}
              <Link
                to="/articulation-before-adjudication"
                className="text-cyan-300/90 hover:text-cyan-200 underline-offset-4 hover:underline transition-colors"
              >
                the emerging category these tools belong to →
              </Link>
            </p>

            {/* Related */}
            <div className="mt-10 pt-6 border-t border-white/[0.06] text-center">
              <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-white/40 mb-2">
                Related
              </p>
              <Link
                to="/ai-detect-narcissistic-abuse"
                className="text-[14px] md:text-[15px] text-cyan-300/85 hover:text-cyan-200 transition-colors underline-offset-4 hover:underline"
              >
                Read: How AI Detects Narcissistic Abuse in Text Conversations →
              </Link>
            </div>

            <div className="mt-8 text-center">
              <a href={SECONDARY_CTA_URL} className={ctaClass}>
                Open GreyRockAI →
              </a>
            </div>
          </section>
        </article>
      </main>

      <footer className="border-t border-cyan-400/10 mt-16 py-6 px-5 text-center text-[11px] font-mono text-white/30 tracking-wider">
        <div>GREYROCKAI® · BEHAVIORAL INTELLIGENCE</div>
        <div className="mt-1.5 flex items-center justify-center gap-4">
          <Link to="/" className="hover:text-cyan-300/80 transition-colors">Home</Link>
          <span>·</span>
          <Link to="/privacy" className="hover:text-cyan-300/80 transition-colors">Privacy</Link>
          <span>·</span>
          <Link to="/terms" className="hover:text-cyan-300/80 transition-colors">Terms</Link>
          <span>·</span>
          <Link to="/contact" className="hover:text-cyan-300/80 transition-colors">Contact</Link>
        </div>
      </footer>

      <style>{`
        @keyframes cyanPulseLocal {
          0%, 100% { box-shadow: 0 0 40px -6px rgba(34,211,238,0.7); }
          50% { box-shadow: 0 0 60px -2px rgba(34,211,238,1); }
        }
        .animate-cyanPulse { animation: cyanPulseLocal 3.2s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

export default GreyRockMethodExamples;
